import { v4 as uuidv4 } from 'uuid';
import { FormDefinition, Question, Section } from '../types/form-engine.types';
import { createQuestion, normalizeLoadedQuestion, withPositions } from './question-factory';

/** Storage shape shared by every form-like schema (PmForm, SurveyForm). */
export interface StoredFormDefinition {
  HasSections?: boolean;
  FormDataJson?: string;
  SectionsJson?: string;
}

const parseArray = <T>(json?: string): T[] => {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

export const emptyFormDefinition = (): FormDefinition => ({ HasSections: false, FormData: [createQuestion()], Sections: [] });

export const parseFormDefinition = (stored?: StoredFormDefinition): FormDefinition => {
  if (!stored) return emptyFormDefinition();
  const sections = parseArray<Section>(stored.SectionsJson).map((section) => ({
    ...section,
    Description: section.Description ?? '',
    Questions: (section.Questions ?? []).map((q) => normalizeLoadedQuestion(q as never)),
  }));
  const formData = parseArray<Question>(stored.FormDataJson).map((q) => normalizeLoadedQuestion(q as never));

  const hasSections = Boolean(stored.HasSections) && sections.length > 0;
  return { HasSections: hasSections, FormData: hasSections ? [] : formData, Sections: hasSections ? sections : [] };
};

/** Answers never belong to a form definition: strip them so templates stay clean. */
const stripAnswer = (question: Question): Question => {
  const {
    Answer: _answer,
    Rating: _rating,
    McOptionsValue: _mc,
    CbOptionsValues: _cb,
    Comments: _comments,
    ...definition
  } = question;
  return {
    ...definition,
    Options: question.FormatType === 'multiple-choice' || question.FormatType === 'checkboxes' ? (question.Options ?? []) : [],
    LikertStatements: (question.LikertStatements ?? []).map((s) => ({ ...s, Value: [] })),
  };
};

const cleanQuestion = (question: Question): Question => ({
  ...stripAnswer(question),
  Question: question.Question.trim(),
  QuestionDescription: question.QuestionDescription?.trim() ?? '',
  Options: (
    question.FormatType === 'multiple-choice' || question.FormatType === 'checkboxes' ? (question.Options ?? []) : []
  ).map((option) => ({ ...option, Value: option.Value || option.Label, Action: option.Action ?? 'continue' })),
});

export const serializeFormDefinition = (definition: FormDefinition): Required<StoredFormDefinition> => {
  if (definition.HasSections) {
    const sections = withPositions(definition.Sections).map((section, index) => ({
      ...section,
      Title: section.Title.trim() || `Section ${index + 1}`,
      Description: section.Description.trim(),
      Questions: withPositions(section.Questions).map((q) => ({ ...cleanQuestion(q), SectionId: section.SectionId })),
    }));
    return { HasSections: true, FormDataJson: '[]', SectionsJson: JSON.stringify(sections) };
  }
  const questions = withPositions(definition.FormData).map((q) => ({ ...cleanQuestion(q), SectionId: undefined }));
  return { HasSections: false, FormDataJson: JSON.stringify(questions), SectionsJson: '[]' };
};

/** Copy for "duplicate form": every question/section gets a new id and jump targets follow the new section ids. */
export const cloneFormDefinition = (definition: FormDefinition): FormDefinition => {
  const sectionIds = new Map(definition.Sections.map((section) => [section.SectionId, uuidv4()]));

  const cloneQuestion = (question: Question, sectionId?: string): Question => ({
    ...stripAnswer(question),
    ItemId: uuidv4(),
    SectionId: sectionId,
    Options: (question.Options ?? []).map((option) => ({
      ...option,
      TargetSectionId: option.TargetSectionId ? sectionIds.get(option.TargetSectionId) : undefined,
    })),
  });

  return {
    HasSections: definition.HasSections,
    FormData: definition.FormData.map((q) => cloneQuestion(q)),
    Sections: definition.Sections.map((section) => {
      const newId = sectionIds.get(section.SectionId) as string;
      return { ...section, SectionId: newId, Questions: section.Questions.map((q) => cloneQuestion(q, newId)) };
    }),
  };
};
